import { ZodInstant } from '@thinkalpha/common/schemas/codecs.js';
import type { FilingsQuery } from '@thinkalpha/quant-ws-client/client.js';
import type { Filing } from '@thinkalpha/quant-ws-client/contracts/filings.js';
import { createInstance } from 'src/api';
import { appConfig } from 'src/lib/config';
import { quantWsClient } from 'src/lib/newQuant';
import { container } from 'src/StaticContainer';
import { z } from 'zod';

const legacyQuantApi = createInstance({ baseURL: appConfig.quantApi });

export const log = container.get('Logger').getSubLogger({ name: 'FilingsApi' });

const LegacyFiling = z.object({
    FilingId: z.string(),
    SubmittedTimestamp: ZodInstant,
    UploadedTimestamp: ZodInstant,
    Symbol: z.string(),
    Type: z.string(),
    FilingName: z.string(),
    EventDetails: z.string().nullable(),
    Link: z.string(),
    IsSummarizable: z.boolean(),
});

const LegacyFilingsResponse = z.object({
    response: z.array(LegacyFiling).nullable(),
});

export type GetFilingsQuery = Omit<FilingsQuery, 'symbols'> & {
    symbols: string[];
    searchString?: string;
};

async function getLegacyKeywordSearchFilings({
    symbols,
    types,
    tags,
    start,
    end,
    limit,
    sortBy,
    cursorTimestamp,
    cursorId,
    direction,
    searchString,
}: GetFilingsQuery & { searchString: string }): Promise<Filing[]> {
    // Temporary compatibility path: quant-ws does not yet support filing keyword search.
    // Keep keyword searches on quant-ng until the backend capability has a replacement.
    const res = await legacyQuantApi.post<unknown>('/v1/filings/query', {
        symbols,
        filingTypes: types,
        searchString,
        tags8K: tags,
        start,
        end,
        limit,
        sortBy: sortBy === 'uploadedAt' ? 'uploadedTimestamp' : 'submittedTimestamp',
        pagination: cursorTimestamp
            ? {
                  submittedTimestamp: cursorTimestamp,
                  filingId: cursorId,
                  direction: direction === 'newer' ? 'find_newer' : 'find_older',
              }
            : undefined,
    });
    const filings = LegacyFilingsResponse.parse(res.data).response ?? [];

    return filings.map(
        (filing) =>
            ({
                id: filing.FilingId,
                submittedAt: filing.SubmittedTimestamp,
                uploadedAt: filing.UploadedTimestamp,
                symbol: filing.Symbol,
                type: filing.Type,
                filingName: filing.FilingName,
                eventDetails: filing.EventDetails ?? undefined,
                link: filing.Link,
                isSummarizable: filing.IsSummarizable,
            }) satisfies Filing,
    );
}

export async function getFilings({
    symbols,
    types = [],
    searchString,
    tags,
    start,
    end,
    cursorTimestamp,
    cursorId,
    direction = 'older',
    limit,
    sortBy = 'submittedAt',
}: GetFilingsQuery): Promise<Filing[]> {
    if (symbols.length === 0) {
        return [];
    }

    const keywordSearch = searchString?.trim();
    if (keywordSearch) {
        return getLegacyKeywordSearchFilings({
            symbols,
            types,
            tags,
            start,
            end,
            cursorTimestamp,
            cursorId,
            direction,
            limit,
            sortBy,
            searchString: keywordSearch,
        });
    }

    return quantWsClient.getFilings({
        symbols,
        types,
        tags,
        start,
        end,
        cursorTimestamp,
        cursorId,
        direction,
        limit,
        sortBy,
    });
}

export async function getFilingTypes(): Promise<string[]> {
    try {
        return await quantWsClient.getFilingTypes();
    } catch (e) {
        log.error('Failed to fetch filing types', { error: e });
        throw e;
    }
}

export async function getFilingTags(): Promise<string[]> {
    try {
        return await quantWsClient.getFilingTags();
    } catch (e) {
        log.error('Failed to fetch filing tags', { error: e });
        throw e;
    }
}

export async function getFilingSummary(filingId: string): Promise<string> {
    try {
        return (await quantWsClient.getFilingSummary(filingId)).response;
    } catch (e) {
        log.error('Failed to fetch filing summary', { error: e, filingId });
        throw e;
    }
}
